import axios from "axios";
import { toast } from "react-toastify";

import config from "../../config";
import { track, clearUser } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { announcePendingChanges } from "../pendingChangesNotice";
import { AUTH_LIMIT, AuthLimitError, authLimitFrom } from "./authLimit";
import { codeErrorFrom } from "./apiCodeError";

const AUTH_SESSION_KEY = "concierge_user";
export const getUserFromCookie = () => {
    const user = sessionStorage.getItem(AUTH_SESSION_KEY);
    return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

// An expired session ends the session with a full document load, which destroys
// every React effect — so the analytics side of it has to happen here, before the
// navigation, or it never happens at all (ANALYTICS.md B3). Order matters: emit the
// event while the user dimensions are still set, then clear them.
let redirecting = false;
const redirectToLogin = () => {
    // Drop the dead session first, every time. Leaving it in storage made the
    // reloaded sign-in page look signed-in: the notifications bridge mounted, its
    // fetch got 401, the refresh failed, and this redirect ran again, a reload
    // loop that sent thousands of requests per tab until the tab was closed.
    sessionStorage.removeItem(AUTH_SESSION_KEY);
    delete axios.defaults.headers.common["Authorization"];
    // Already on a sign-in page: nothing to navigate to (and no flag left set,
    // so a later session in this page still ends properly).
    if (window.location.pathname.startsWith("/auth/")) return;
    // Several reads can fail together; one navigation (and one logout event) is enough.
    if (redirecting) return;
    redirecting = true;
    try {
        track(EVENTS.LOGOUT, { reason: "session_expired" });
        clearUser();
    } catch {
        // Analytics must never be able to break the auth redirect.
    }
    window.location.href = "/auth/login";
};

/** Marks a request whose 401 must not trigger a token refresh (the refresh call itself). */
const SKIP_AUTH_REFRESH = "skipAuthRefresh";

// One refresh at a time: requests that meet a 401 together share it, so a page
// with five reads in flight sends one refresh, not five.
let refreshInFlight: Promise<string | null> | null = null;
// After a refused refresh, don't ask again for a while: a burst of 401s (or a
// component that refetches on error) must not turn into a refresh per request.
const REFRESH_COOLDOWN_MS = 30_000;
let refreshRefusedAt = 0;

/** Renews the access token. Resolves the new token, or null when the server refused. */
const refreshSession = (): Promise<string | null> => {
    if (!refreshInFlight) {
        if (Date.now() - refreshRefusedAt < REFRESH_COOLDOWN_MS) return Promise.resolve(null);
        refreshInFlight = (async () => {
            const user = getUserFromCookie();
            if (!user?.refresh_token) return null;
            try {
                // The supplier's own refresh endpoint (this used to post to the
                // admin one, /api/auth/refresh-token).
                const refresh = await axios.post(
                    "/supplier-api/auth/refresh-token",
                    { refresh_token: user.refresh_token },
                    { [SKIP_AUTH_REFRESH]: true, [AUTH_LIMIT]: true } as any,
                );
                const data = refresh?.data?.data;
                if (!refresh?.data?.status || !data?.access_token) {
                    refreshRefusedAt = Date.now();
                    return null;
                }
                user.refresh_token = data.refresh_token;
                user.access_token = data.access_token;
                sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(user));
                setAuthorization(user.access_token);
                refreshRefusedAt = 0;
                return user.access_token as string;
            } catch (e) {
                // Rate limited (429) or the limiter is down (503): the session is still
                // good, so the caller keeps it, and no cooldown starts (the next 401
                // may refresh again). Every other failure ends it.
                if (e instanceof AuthLimitError) throw e;
                refreshRefusedAt = Date.now();
                return null;
            }
        })().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

export const SESSION_EXPIRED_MESSAGE =
    "Your session has expired, so this change was not saved. Sign in again to continue.";

/**
 * The session is over (the token was refused and so was the refresh).
 *
 * A read can go straight to the sign-in page: nothing is lost. A WRITE cannot —
 * the hard navigation used to drop the form and everything typed into it, with
 * no word to the user. For a write the user stays on the page with what they
 * typed, the form's own error handling gets a message that says what happened,
 * and a toast offers the way to sign in again. Returns the rejection reason.
 */
const endSession = (config: any): string => {
    const method = String(config?.method || "get").toLowerCase();
    const isWrite = !["get", "head", "options"].includes(method);
    // No session at all (never signed in, or already signed out): nothing typed
    // belongs to a session that could be renewed — keep the old sign-in redirect.
    if (!isWrite || !getUserFromCookie()) {
        redirectToLogin();
        return "Unauthorized";
    }
    toast.error(SESSION_EXPIRED_MESSAGE + " Click here to sign in.", {
        toastId: "session-expired",
        autoClose: false,
        closeOnClick: false,
        onClick: () => redirectToLogin(),
    });
    return SESSION_EXPIRED_MESSAGE;
};

// Every path below that hands a message back to the UI reports it once. The HTTP
// status ONLY: an API error message can quote a field value, a customer name or a
// document reference, so the text itself never travels (ANALYTICS.md §9). A 401 is
// not reported here — it is a session expiry, already covered by the `logout` event
// above, and it never reaches a component as an error.
const trackApiError = (code: string | number) => {
    try {
        track(EVENTS.ERROR_SHOWN, { error_scope: "api", error_code: code });
    } catch {
        // Analytics must never be able to change how an API error surfaces.
    }
};

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        // Edits to a live listing: say which changes wait for review (one place for every form).
        announcePendingChanges(response?.data);
        return response;
    },
    async (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        let message;

        // Sign-in pages and the refresh call opt in to the rate-limit details
        // (authLimit.ts): status, code and the seconds to wait, not just a string.
        if (error?.config?.[AUTH_LIMIT]) {
            const limited = authLimitFrom(error);
            if (limited) {
                trackApiError(limited.status);
                return Promise.reject(limited);
            }
        }

        if (error && error.response && error.response.status === 404) {
            trackApiError(404);
            return Promise.reject(error.response?.data?.message || "Not found");
        } else if (error && error.response && error.response.status === 401) {
            const config = error.config || {};
            // The refresh call's own 401 is not "another expired request": it is
            // the answer to the refresh. Hand it straight back to the caller below
            // (refreshSession), which ends the session once. Letting it run this
            // handler again started a new refresh for every refused one, a loop that
            // only stopped when the auth rate limiter answered 429.
            if (config[SKIP_AUTH_REFRESH]) {
                return Promise.reject(error);
            }
            if (!config.refreshTokenRequest) {
                let accessToken: string | null;
                try {
                    accessToken = await refreshSession();
                } catch (e) {
                    // Only a refused refresh ends the session; a 429/503 keeps it.
                    return Promise.reject(e instanceof AuthLimitError ? e.message : "Unauthorized");
                }
                if (accessToken) {
                    // Retry once with the renewed token.
                    config.refreshTokenRequest = true;
                    config.headers = config.headers || {};
                    config.headers["Authorization"] = `Bearer ${accessToken}`;
                    return axios.request(config);
                }
            }
            return Promise.reject(endSession(config));
        } else if (!error.response) {
            trackApiError("network");
            // Network error / no response (backend down, CORS, timeout). Reject
            // with a readable STRING — never the raw Error object, which a
            // component might try to render as a child ("Objects are not valid
            // as a React child (found: [object Error])").
            return Promise.reject(
                (typeof error?.message === "string" && error.message)
                    || "Network error — please check your connection and try again."
            );
        } else {
            switch (error.response.status) {
                case 403:
                    message = error.response?.data?.message || "Access Forbidden";
                    break;
                case 404:
                    message = "Sorry! the data you are looking for could not be found";
                    break;
                default: {
                    message =
                        error.response?.data?.message
                            || (typeof error.message === "string" && error.message)
                            || "Something went wrong. Please try again.";
                    // Validation failures carry the per-field details in `data`
                    // (e.g. "homeBase must be ..."); surface them so forms can
                    // tell the user WHAT failed instead of a bare "validation fail".
                    const details = error.response?.data?.data;
                    if (typeof details === "string" && details && details !== message) {
                        message = `${message}: ${details}`;
                    }
                }
            }
            trackApiError(error.response.status);
            // Opted-in callers get the backend's code with the same message (apiCodeError.ts).
            return Promise.reject(codeErrorFrom(error, message) || message);
        }
    }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    else delete axios.defaults.headers.common["Authorization"];
};

class APICore {
    /**
     * Fetches data from given url
     */
    get = (url: string, params: any, cancelToken?: any) => {
        // Let axios serialize params — its serializer URL-encodes keys/values and
        // skips undefined entries. Hand-concatenating "k=" + v doubled the query
        // string (axios appended it again from `params`) and let user input like
        // `&admin=true` smuggle extra parameters into the backend.
        return axios.get(url, {params, cancelToken});
    };

    getFile = (url: string, params: any) => {
        return axios.get(url, {params, responseType: "blob"});
    };

    getMultiple = (urls: string, params: any) => {
        const reqs: any[] = [];
        for (const url of urls) {
            reqs.push(axios.get(url, {params}));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any, config?: any) => {
        return axios.post(url, data, config);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any, config?: any) => {
        return axios.put(url, data, config);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = data instanceof FormData ? data : (() => {
            const fd = new FormData();
            if (data && typeof data === 'object') {
                Object.keys(data).forEach((k) => {
                    const v: any = (data as any)[k];
                    // If value is array, append each item with same key
                    if (Array.isArray(v)) {
                        v.forEach((item) => fd.append(k, item));
                    } else {
                        fd.append(k, v);
                    }
                });
            }
            return fd;
        })();

        const config = {
            headers: {
                // Let axios set the correct multipart boundary automatically
                'Content-Type': 'multipart/form-data',
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = data instanceof FormData ? data : (() => {
            const fd = new FormData();
            if (data && typeof data === 'object') {
                Object.keys(data).forEach((k) => {
                    const v: any = (data as any)[k];
                    if (Array.isArray(v)) {
                        v.forEach((item) => fd.append(k, item));
                    } else {
                        fd.append(k, v);
                    }
                });
            }
            return fd;
        })();

        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const user = this.getLoggedInUser();
        if (!user) {
            return false;
        }
        setAuthorization(user.access_token);
        return true;
    };

    setLoggedInUser = (session: any) => {
        if (session)
            sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        else {
            sessionStorage.removeItem(AUTH_SESSION_KEY);
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromCookie();
    };

    /**
     * Merge fields into the stored session user (e.g. a confirmed new phone).
     * The session is the FLAT login payload ({access_token, email,
     * phone_number, ...}); this used to destructure a `user` key that does not
     * exist, so any call would have dropped every field but the tokens.
     */
    setUserInSession = (modifiedUser: any) => {
        const userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
        if (userInfo) {
            this.setLoggedInUser({ ...JSON.parse(userInfo), ...modifiedUser });
        }
    };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
    const {token} = user;
    if (token) {
        setAuthorization(token);
    }
}

export {APICore, setAuthorization};
